import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/react-hooks';
import {
  loog,
  getCookie,
  getPath,
  parseErrorResponse,
} from '@express-labs/raven-tools';
import { login, REMOVE_SELLER_FROM_BAG } from '../../../../graphql/mutation';
import { getOrderSummary } from '../../../../queries';
import LoginForm from './LoginForm';
import GoogleRecaptchaEnterprise from '../../../../utils/googleRecaptchaEnterprise';
import { analyze } from '../../../../helpers';

function LoginFormVM(props) {
  const [captchaToken, setCaptchaToken] = useState();
  const [captchaError, setCaptchaError] = useState();
  const [hasSignInError, setHasSignInError] = useState();
  const [signInErrorMesg, setSignInErrorMesg] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const cjeCookie = getCookie('cje');
  const hasCJECookie = !!cjeCookie;
  const { pathname } = window.location;
  const isBag = pathname.includes('/bag/');
  const captchInputRef = useRef();
  const decideShowCaptchaVal = (val) => {
    const captcha = getPath(window, 'FLAGS.Captcha', false);
    const alwaysCaptcha = getPath(window, 'FLAGS.AlwaysCaptcha', false);
    if (captcha && alwaysCaptcha) {
      return true;
    }
    return val;
  };
  const [showCaptcha, setShowCaptcha] = useState(decideShowCaptchaVal(false));

  const [onApplyChanges] = useMutation(login, {
    refetchQueries: [
      {
        query: getOrderSummary,
        variables: { shouldRecalculateTax: false },
      }],
  });

  const [onRemoveSellerFromBag] = useMutation(REMOVE_SELLER_FROM_BAG, {
    update(cache, { data: { removeSellerFromBag } }) {
      const orderData = cache.readQuery({ query: getOrderSummary });

      if (removeSellerFromBag) {
        cache.writeQuery({
          query: getOrderSummary,
          data: {
            orderSummary: { ...orderData, ...removeSellerFromBag },
          },
        });
      }
    },
  });

  const resetSignInError = (message, signInErrorState) => {
    setHasSignInError(signInErrorState);
    setSignInErrorMesg(message);
  };

  /* The below code coverage will be covered later so commenting that for now */
  /* istanbul ignore next */
  const captchaExpiredCallback = async () => {
    setCaptchaToken('');
    setHasSignInError(false);
  };
  /* istanbul ignore next */
  const captchaVerifiedCallback = async (token) => {
    setCaptchaToken(token);
    setHasSignInError(false);
  };

  const isRecaptchaEnterpriseError = (error) => {
    if (error) {
      const stringStatus = `${error}`.search('login - forbidden');
      return stringStatus !== -1;
    }
    return false;
  };

  const handleFormSubmit = async (values) => {
    const isCaptchaEnabled = !!window?.FLAGS?.Captcha;
    const isRecaptchaEnterpriseEnabled = !!window?.FLAGS?.CaptchaEnterprise;
    const isRecaptchaEnterpriseListenMode = !!window?.FLAGS?.CaptchaEnterpriseListenMode;
    let googleReCaptchaToken = '';
    let recaptchaEnterpriseData = {};

    if (isRecaptchaEnterpriseEnabled && !isCaptchaEnabled) {
      const {
        getGoogleReCaptchaEnterpriseToken,
        // eslint-disable-next-line import/no-named-as-default-member
        ACTION_LOGIN_SIGN_IN_BTN,
      } = GoogleRecaptchaEnterprise;
      const action = ACTION_LOGIN_SIGN_IN_BTN;

      googleReCaptchaToken = await getGoogleReCaptchaEnterpriseToken(action);

      recaptchaEnterpriseData = {
        googleReCaptchaToken,
        action,
      };
    }

    setHasSignInError(false);
    setSubmitButtonDisabled(false);
    if (showCaptcha && !captchaToken) {
      setCaptchaError("Please prove you're not a robot");
    } else {
      setIsFetching(true);
      setCaptchaError('');
      onApplyChanges({
        variables: {
          loginName: values.email,
          password: values.password,
          captchaToken,
          doNotMerge: false,
          ...recaptchaEnterpriseData,
        },
      })
        .then((response) => {
          setIsFetching(false);
          const status = response.data && response.data.login && response.data.login.loginStatus;
          if (status && status.toLowerCase() === 'logged_in') {
            analyze({
              name: 'AccountReady',
            });
            /* istanbul ignore else */
            if (window.Heartbeat) {
              window.Heartbeat.beat();
              window.location.assign('/login');
            }
            if (!hasCJECookie && isBag) {
              onRemoveSellerFromBag().then(() => {
              });
            }
            Promise.resolve('success');
            setHasSignInError(false);
          } else {
            setHasSignInError(true);
            throw new Error('has error');
          }
        })
        .catch((error) => {
          loog(`Log in error: ${error}`, 'info', error);
          const errorResponse = parseErrorResponse(error);
          const errorMessage = getPath(errorResponse, 'message', null);
          if (error
            && isRecaptchaEnterpriseEnabled
            && !isRecaptchaEnterpriseListenMode
            && isRecaptchaEnterpriseError(error)
          ) {
            setSubmitButtonDisabled(true);
            setSignInErrorMesg(errorMessage);
            setHasSignInError(true);
            setIsFetching(false);
            Promise.resolve('invalid');
          }

          /* istanbul ignore next */
          if (errorResponse) {
            if (errorResponse.showCaptcha) {
              setShowCaptcha(decideShowCaptchaVal(true));
              setCaptchaToken('');
            }
            if (errorMessage) {
              setSubmitButtonDisabled(true);
              setSignInErrorMesg(errorMessage);
              setHasSignInError(true);
              setIsFetching(false);
            }
          }
        });
    }
  };
  const isRecaptchaEnterpriseEnabled = !!window?.FLAGS?.CaptchaEnterprise;

  const mapProps = {
    captchaError,
    hasSignInError,
    signInErrorMesg,
    handleFormSubmit,
    captchaVerifiedCallback,
    captchaExpiredCallback,
    captchInputRef,
    showCaptcha,
    isFetching,
    isRecaptchaEnterpriseEnabled,
    submitButtonDisabled,
    resetSignInError,
    vendorFlags: props.vendorFlags,
  };
  return <LoginForm {...mapProps} />;
}

LoginFormVM.propTypes = {
  vendorFlags: PropTypes.shape({
    Captcha: PropTypes.bool,
    AlwaysCaptcha: PropTypes.bool,
    CaptchaEnterprise: PropTypes.bool,
    CaptchaEnterpriseListenMode: PropTypes.bool,
  }),
};

LoginFormVM.defaultProps = {
  vendorFlags: {
    Captcha: false,
    AlwaysCaptcha: false,
    CaptchaEnterprise: true,
    CaptchaEnterpriseListenMode: true,
  },
};

export default LoginFormVM;
